// Importación dinámica para evitar problemas de tipos
let GoogleGenerativeAI: any;
let genAI: any;

// Configurar la API key de Gemini
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || 'AIzaSyDK0eROzVWQXCWbpf2uCmKFY_xqvdxGtUM';

// Inicializar la librería de forma dinámica
const initializeGemini = async () => {
  if (!GoogleGenerativeAI) {
    const module = await import('@google/generative-ai');
    GoogleGenerativeAI = module.GoogleGenerativeAI;
    genAI = new GoogleGenerativeAI(API_KEY);
  }
  return genAI;
};

export interface DocumentAnalysis {
  summary: string;
  classification: string;
  confidence: number;
  keyTopics: string[];
  documentType: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ChatSession {
  id: string;
  documentId: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
}

class GeminiService {
  private async getModel() {
    const genAI = await initializeGemini();
    return genAI.getGenerativeModel({ model: 'gemini-2.5-flash' });
  }

  /**
   * Analiza un documento PDF y genera un resumen y clasificación
   */
  async analyzeDocument(pdfData: ArrayBuffer): Promise<DocumentAnalysis> {
    try {
      console.log('🤖 Iniciando análisis de documento con Gemini...');
      
      // Convertir ArrayBuffer a base64
      const base64Data = this.arrayBufferToBase64(pdfData);
      
                        const prompt = `
                    Analyze this PDF document and provide the following information in JSON format:

                    {
                      "summary": "Concise summary of the document (maximum 200 words)",
                      "classification": "Document type classification (e.g., Contract, Invoice, Report, etc.)",
                      "confidence": 0.95,
                      "keyTopics": ["topic1", "topic2", "topic3"],
                      "documentType": "Specific document type"
                    }

                    Rules:
                    - Respond ONLY in valid JSON format
                    - Do not include additional explanations
                    - The summary should be clear and concise
                    - The classification should be specific and useful
                    - Key topics should be relevant to the document
                  `;

      const model = await this.getModel();
      const result = await model.generateContent([
        prompt,
        {
          inlineData: {
            mimeType: 'application/pdf',
            data: base64Data
          }
        }
      ]);

      const response = await result.response;
      const text = response.text();
      
      console.log('🤖 Respuesta de Gemini:', text);
      
      // Limpiar la respuesta y parsear JSON
      const cleanText = this.cleanJsonResponse(text);
      const analysis = JSON.parse(cleanText);
      
      return analysis as DocumentAnalysis;
      
    } catch (error) {
      console.error('❌ Error analizando documento:', error);
      throw new Error('Failed to analyze document with AI');
    }
  }

  /**
   * Inicia una nueva sesión de chat sobre el documento
   */
  async startChat(pdfData: ArrayBuffer, initialMessage?: string): Promise<ChatSession> {
    try {
      console.log('💬 Iniciando sesión de chat con Gemini...');
      
      const base64Data = this.arrayBufferToBase64(pdfData);
      const sessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const systemPrompt = `
        You are an expert document analysis assistant.
        You have access to the following PDF document and can answer questions about it.
        Provide clear, accurate, and helpful responses based on the document content.
        If you cannot find the information in the document, clearly indicate it.
      `;

      const userMessage = initialMessage || "Hello, can you give me a summary of this document?";

      const model = await this.getModel();
      const result = await model.generateContent([
        systemPrompt,
        {
          inlineData: {
            mimeType: 'application/pdf',
            data: base64Data
          }
        },
        userMessage
      ]);

      const response = await result.response;
      const assistantMessage = response.text();

      const session: ChatSession = {
        id: sessionId,
        documentId: `doc_${Date.now()}`,
        messages: [
          {
            role: 'user',
            content: userMessage,
            timestamp: new Date()
          },
          {
            role: 'assistant',
            content: assistantMessage,
            timestamp: new Date()
          }
        ],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('💬 Sesión de chat iniciada:', sessionId);
      return session;
      
    } catch (error) {
      console.error('❌ Error iniciando chat:', error);
      throw new Error('Failed to start chat session');
    }
  }

  /**
   * Envía un mensaje en una sesión de chat existente
   */
  async sendMessage(session: ChatSession, message: string): Promise<ChatMessage> {
    try {
      console.log('💬 Enviando mensaje en sesión:', session.id);
      
      // Construir el contexto del chat como texto
      const chatContext = session.messages.map(msg => 
        `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
      ).join('\n\n');
      
      const fullPrompt = `
        Previous conversation context:
        ${chatContext}
        
        User: ${message}
        
        Assistant: 
      `;

      const model = await this.getModel();
      const result = await model.generateContent(fullPrompt);
      const response = await result.response;
      const assistantMessage = response.text();

      const newMessage: ChatMessage = {
        role: 'assistant',
        content: assistantMessage,
        timestamp: new Date()
      };

      console.log('💬 Respuesta recibida');
      return newMessage;
      
    } catch (error) {
      console.error('❌ Error enviando mensaje:', error);
      throw new Error('Failed to send message');
    }
  }

  /**
   * Convierte ArrayBuffer a base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Limpia la respuesta JSON de Gemini
   */
  private cleanJsonResponse(text: string): string {
    // Remover markdown code blocks si existen
    let cleanText = text.trim();
    
    if (cleanText.startsWith('```json')) {
      cleanText = cleanText.substring(7);
    } else if (cleanText.startsWith('```')) {
      cleanText = cleanText.substring(3);
    }
    
    if (cleanText.endsWith('```')) {
      cleanText = cleanText.substring(0, cleanText.length - 3);
    }
    
    return cleanText.trim();
  }
}

export const geminiService = new GeminiService();
export default geminiService; 